import * as marietta3d from '../..';
import * as marietta from '../../../marietta';

/*------------------------------------------------------
* Line
*/
export default class RoomEdge {
    public s: marietta.core.Point;

    public e: marietta.core.Point;

    constructor() {
      this.s = null;
      this.e = null;
    }

    //------------------------------------------------------
    //
    public Length(): number {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.Length : 座標が設定されていません');
      }
      return this.s.distanceTo(this.e);
    }

    /*------------------------------------------------------
    */
    public CopyFrom(ln: RoomEdge) {
      this.s = new marietta.core.Point(ln.s.x, ln.s.y, ln.s.flag);
      this.e = new marietta.core.Point(ln.e.x, ln.e.y, ln.e.flag);
    }

    /*------------------------------------------------------
    */
    public Init(x1: number, y1: number, x2: number, y2: number) {
      this.s = new marietta.core.Point(x1, y1, 0);
      this.e = new marietta.core.Point(x2, y2, 0);
    }

    /*------------------------------------------------------
    */
    public CopyPointFrom(s: marietta.core.Point, e: marietta.core.Point) {
      this.s = new marietta.core.Point(s.x, s.y, s.flag);
      this.e = new marietta.core.Point(e.x, e.y, e.flag);
    }

    /*------------------------------------------------------
    */
    public GetPointFrom(s: marietta.core.Point, e: marietta.core.Point) {
      this.s = s;
      this.e = e;
    }

    /*------------------------------------------------------
    * 点と線分の位置関係。
    *
    * 線分を直線と考えて、点と直線の垂直距離を返す。
    * 始点から終点に向かうベクトルの左側に点があるときプラスの距離、右側はマイナス距離。
    */
    public DistancePtoLine(p: marietta.core.Point): number {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.Distance_PtoLine : 座標が設定されていません');
      }
      let a = this.s.y - this.e.y;
      let b = this.e.x - this.s.x;
      const s = Math.sqrt(a * a + b * b);
      let d: any;
      if (s < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        /* 線分の長さがほとんどゼロの時は、点と始点の距離を返す */
        d = p.distanceTo(this.s);
      } else {
        a /= s;
        b /= s;
        const c = this.s.x * (-a) - b * this.s.y;
        d = a * p.x + b * p.y + c;
      }
      return d;
    }

    /*------------------------------------------------------
    * 点と線分の垂直距離。
    *
    * 点からの垂直線が線分内に交点を持つ場合は、その交点との距離を返す。
    * 上記以外は、端点からの近いほうの距離を返す。
    */
    public DistancePtoEdge(p: marietta.core.Point): number {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.Distance_PtoEdge : 座標が設定されていません');
      }
      /* 点からの垂直線が線分内に交点を持つ場合は、その交点との距離を返す */
      const cr = new marietta.core.Point(0, 0, 0);
      if (this.VerticalIntersection(p, cr) === true) {
        return p.distanceTo(cr);
      }
      // 上記以外は、端点からの近いほうの距離を返す
      const d1 = p.distanceTo(this.s);
      const d2 = p.distanceTo(this.e);
      if (d1 < d2) {
        return d1;
      }
      return d2;
    }

    /*------------------------------------------------------
    * 点から線分に垂線をおろした時の交点の計算
    *
    * 線分内に交点があるとき：true
    */
    public VerticalIntersection(p: marietta.core.Point, cr: marietta.core.Point): boolean {
      const crPoint = cr;
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.VerticalIntersection : 座標が設定されていません');
      }
      let wy = this.e.y - this.s.y;
      let wx = this.e.x - this.s.x;
      const d = wx * wx + wy * wy;
      if (d < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        crPoint.copyFrom(this.s);
        return false;
      }
      const t = ((p.x - this.s.x) * wx + (p.y - this.s.y) * wy) / (d);
      wx = (wx * t + this.s.x);
      wy = (wy * t + this.s.y);
      crPoint.x = wx;
      crPoint.y = wy;
      if ((t > 0.0) && (t < 1.0)) {
        return true;
      }
      return false;
    }

    /*------------------------------------------------------
    * 線分の始点／終点を伸縮させる。
    *
    * （soff, eoff: +延長、-短縮）
    */
    public IncreaseLine(soff: number, eoff: number): boolean {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.IncreaseLine : 座標が設定されていません');
      }
      const x = this.e.x - this.s.x;
      const y = this.e.y - this.s.y;
      const d = Math.sqrt(x * x + y * y);
      if (d < marietta3d.AutoGen.utils.ALMOST_ZERO) return false;
      const st = (d + soff) / d;
      const et = (d + eoff) / d;

      const wx = this.e.x - x * st;
      const wy = this.e.y - y * st;
      this.e.x = this.s.x + x * et;
      this.e.y = this.s.y + y * et;
      this.s.x = wx;
      this.s.y = wy;
      return true;
    }

    /*------------------------------------------------------
    * 線分のオフセット座標を計算する
    *
    * offset : (+)線分の左側、(-)右側
    * 戻り値：false 線分の長さが短すぎるためoffset出来ない。
    */
    public GetOffsetEdge(off: number): boolean {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.GetOffsetEdge : 座標が設定されていません');
      }
      let x = this.e.x - this.s.x;
      let y = this.e.y - this.s.y;
      const d = Math.sqrt(x * x + y * y);
      if (d < marietta3d.AutoGen.utils.ALMOST_ZERO) return false;
      const tc = x / d;
      const ts = y / d;
      x = (-off * ts + this.s.x);
      y = (off * tc + this.s.y);
      const x2 = (d * tc - off * ts + this.s.x);
      const y2 = (d * ts + off * tc + this.s.y);
      this.s.x = x;
      this.s.y = y;
      this.e.x = x2;
      this.e.y = y2;
      return true;
    }

    /*------------------------------------------------------
    * 線分内のpの位置（始点：ゼロ、終点：+1としたときの位置係数）
    */
    public LocationPtOnLine(p: marietta.core.Point): number {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.LocationPtOnLine : 座標が設定されていません');
      }
      const dx = this.e.x - this.s.x;
      const dy = this.e.y - this.s.y;
      let t = Number.MAX_VALUE;// MAX_FLOAT;
      if (Math.abs(dx) > Math.abs(dy)) {
        if (Math.abs(dx) >= 0.01) t = (p.x - this.s.x) / dx;
      } else if (Math.abs(dy) >= 0.01) t = (p.y - this.s.y) / dy;
      return t;
    }

    /*------------------------------------------------------
    * 点が線分上に乗っているか。乗っている：true。
    *
    * 戻り値：true = 線分内に乗っている。false = 線分上に無い。
    */
    public IsPtOnEdge(p: marietta.core.Point): boolean {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.IsPtOnEdge : 座標が設定されていません');
      }
      const len = this.DistancePtoLine(p);
      if (Math.abs(len) < 0.0001) {
        const t = this.LocationPtOnLine(p);
        if ((t < 0.0) || (t > 1.0)) {
          return false;
        }
        return true;
      }
      return false;
    }

    /*------------------------------------------------------
    * 線分と点の関係を求める
    *
    * Return Code -3:直線の始終点が近過ぎて計算不可能
    * -2:始点から終点向かう直線の左側にある
    * -1:始点から終点向かう直線の右側にある
    * 0:直線上にある
    * 1:直線の始点上にある
    * 2:直線の終点上にある
    * 3:直線の始点の外にある
    * 4:直線の終点の外にある
    *
    *  ◎TOLが0.0の場合,default値2.0の値が入る
    *
    * public RC_POEZ2 = {
    *    IMPOSIBLE_SHORT: -3,
    *    LEFTSIDE_OF_START: -2,
    *    RIGHTSIDE_OF_END: -1,
    *    ON_EDGE: 0,
    *    ON_START: 1,
    *    ON_END: 2,
    *    OUTSIDE_START: 3,
    *    OUTSIDE_END: 4
    * }
    */
    /*------------------------------------------------------
    */
    public IsPtOnEdgeZ2(pc: marietta.core.Point, TOL: number): number {
      let tol = TOL;
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.IsPtOnEdgeZ2 : 座標が設定されていません');
      }
      const TOLDEF = 2.0;
      const v = new marietta.core.Point(0, 0, 0);
      const rc = new marietta.core.Point(0, 0, 0);
      if (Math.abs(tol) < 0.1) {
        tol = TOLDEF;
      }
      const d = v.unitVectorZ2(this.s, this.e);
      if (d < tol) {
        return -3; /* 直線の始終点が近過ぎて計算不可能 */
      }
      pc.isPtOnEdgeVector(this.s, v, rc);
      const dot = rc.x;
      const crs = rc.y;
      if (crs > tol) return -2; /* 直線の始終点の左側にある */
      if (crs < -tol) return -1; /* 直線の始終点の右側にある */
      if (dot < -tol) return 3; /* 直線の始点の外にある */
      if (dot <= tol) return 1; /* 直線の始点上にある */
      if (dot < d - tol) return 0; /* 直線中にある */
      if (dot <= d + tol) return 2; /* 直線の終点上にある */
      return 4; /* 直線の終点の外にある */
    }

    /*------------------------------------------------------
    * ２つの線分の重なり部分の長さ
    */
    public IsOverlapLine(ln: RoomEdge): number {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.IsOverlapLine : 座標が設定されていません');
      }
      let len = this.DistancePtoLine(ln.s);
      if (Math.abs(len) > marietta3d.AutoGen.utils.ALMOST_ZERO) { /* ln.s が(this)の線上にない */
        return 0.0;
      }
      len = this.DistancePtoLine(ln.e);
      if (Math.abs(len) > marietta3d.AutoGen.utils.ALMOST_ZERO) { /* ln.e が(this)の線上にない */
        return 0.0;
      }
      let ts = this.LocationPtOnLine(ln.s); /* 線分(this)に対する ln.s の位置 */
      let te = this.LocationPtOnLine(ln.e); /* 線分(this)に対する ln.e の位置 */
      if (ts < te) {
        if ((te < 0.0) || (ts > 1.0)) {
          return 0.0;
        }
        if (ts < 0.0) {
          if (te < 1.0) {
            return this.s.distanceTo(ln.e);
          }
          return this.s.distanceTo(this.e);
        }
        if (te < 1.0) {
          return ln.s.distanceTo(ln.e);
        }
        return ln.s.distanceTo(this.e);
      }
      const w: number = ts;
      ts = te;
      te = w;
      if ((te < 0.0) || (ts > 1.0)) {
        return 0.0;
      }
      if (ts < 0.0) {
        if (te < 1.0) {
          return this.s.distanceTo(ln.s);
        }
        return this.s.distanceTo(this.e);
      }
      if (te < 1.0) {
        return ln.e.distanceTo(ln.s);
      }
      return ln.e.distanceTo(this.e);
    }

    /*------------------------------------------------------
    * ２線分の交差判定および交点の計算
    *
    * 注意：線分として扱う
    * 戻り値：true = 交点が存在する。false = 平行もしくは線分外で交差
    */
    public IntersectionEdge(ln: RoomEdge, cr: marietta.core.Point): boolean {
      const crPoint = cr;
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.IntersectionEdge : 座標が設定されていません');
      }
      const fa1 = this.DistancePtoLine(ln.s);
      const fb1 = this.DistancePtoLine(ln.e);
      const fa2 = ln.DistancePtoLine(this.s);
      const fb2 = ln.DistancePtoLine(this.e);
      const fab1 = fa1 * fb1;
      const fab2 = fa2 * fb2;
      if (Math.abs(fa1 - fb1) < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        /* ２線分が平行 */
        return false;
      }
      if ((fab1 < 0) && (fab2 < 0)) {
        const t = fa1 / (fa1 - fb1);
        crPoint.x = (ln.e.x - ln.s.x) * t + ln.s.x;
        crPoint.y = (ln.e.y - ln.s.y) * t + ln.s.y;
        return true;
      }
      return false;
    }

    /*------------------------------------------------------
    * ２線分の交差判定および交点の計算。
    *
    * 端点の一致は marietta3d.AutoGen.utils.ALMOST_ZERO で判定
    */
    public IntersectionEdge2(ln: RoomEdge, cr: marietta.core.Point): boolean {
      const crPoint = cr;
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.IntersectionEdge2 : 座標が設定されていません');
      }
      const fa1 = this.DistancePtoLine(ln.s);
      const fb1 = this.DistancePtoLine(ln.e);
      const fa2 = ln.DistancePtoLine(this.s);
      const fb2 = ln.DistancePtoLine(this.e);
      const fab1 = fa1 * fb1;
      const fab2 = fa2 * fb2;
      let t: any;
      if ((fab1 < 0) && (fab2 < 0)) {
        t = fa1 / (fa1 - fb1);
        crPoint.x = (ln.e.x - ln.s.x) * t + ln.s.x;
        crPoint.y = (ln.e.y - ln.s.y) * t + ln.s.y;
        return true;
      }
      const length1 = this.s.distanceTo(this.e);
      let primit: number;
      if (Math.abs(length1) < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        primit = Number.MIN_VALUE;
      } else {
        primit = Math.abs(marietta3d.AutoGen.utils.ALMOST_ZERO / length1);
      }
      if (Math.abs(fa1) < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        t = this.LocationPtOnLine(ln.s);
        if ((-primit <= t) && (t <= (1.0 + primit))) {
          crPoint.copyFrom(ln.s);
          return true;
        }
      }
      if (Math.abs(fb1) < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        t = this.LocationPtOnLine(ln.e);
        if ((-primit <= t) && (t <= (1.0 + primit))) {
          crPoint.copyFrom(ln.e);
          return true;
        }
      }
      const length2 = ln.s.distanceTo(ln.e);
      if (Math.abs(length2) < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        primit = Number.MIN_VALUE;
      } else {
        primit = Math.abs(marietta3d.AutoGen.utils.ALMOST_ZERO / length2);
      }
      if (Math.abs(fa2) < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        t = ln.LocationPtOnLine(this.s);
        if ((-primit <= t) && (t <= (1.0 + primit))) {
          crPoint.copyFrom(this.s);
          return true;
        }
      }
      if (Math.abs(fb2) < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        t = ln.LocationPtOnLine(this.e);
        if ((-primit <= t) && (t <= (1.0 + primit))) {
          crPoint.copyFrom(this.e);
          return true;
        }
      }
      return false;
    }

    /*------------------------------------------------------
    * ２直線の交点を求める
    *
    * 注意：直線として扱う
    * 戻り値：true = 交点が存在する。false = 平行。
    */
    public IntersectionLine(ln: RoomEdge, cr: marietta.core.Point): boolean {
      const crPoint = cr;
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.IntersectionLine : 座標が設定されていません');
      }
      const a1 = this.DistancePtoLine(ln.s);
      const a2 = this.DistancePtoLine(ln.e);
      if (Math.abs(a1 - a2) < marietta3d.AutoGen.utils.ALMOST_ZERO) return false; /* ２直線が平行 */
      const t = a1 / (a1 - a2);
      crPoint.x = (ln.e.x - ln.s.x) * t + ln.s.x;
      crPoint.y = (ln.e.y - ln.s.y) * t + ln.s.y;
      return true;
    }

    /*------------------------------------------------------
    * ２線分の交点を求む
    *
    * 線分１に対して（r.x）
    *  -1:線分外に交点あり
    *   0:線分内に交点あり
    *   1:交点が始点と同じ
    *   2:交点が終点と同じ
    *
    * 線分２に対して（r.y）
    *  -1:線分外に交点あり
    *   0:線分内に交点あり
    *   1:交点が始点と同じ
    *   2:交点が終点と同じ
    *
    * 戻り値
    * -1:交点なし
    *  0:両線分内に交点あり
    *  1:線分１内に交点あり
    *  2:線分２内に交点あり
    *  3:両線分外に交点あり
    */
    public IntersectionLineZ2(ln: RoomEdge, pc: marietta.core.Point, r: marietta.core.Point, TOL: number): number {
      const pcPoint = pc;
      const rPoint = r;
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.IntersectionLineZ2 : 座標が設定されていません');
      }
      let rc: any;
      const TOLDEF = 2.0;
      let tol = TOL;
      if (Math.abs(tol) < 0.1) tol = TOLDEF;
      // 交点計算
      const q1 = this.e.x - this.s.x;
      const q2 = this.e.y - this.s.y;
      const q3 = ln.s.y - ln.e.y;
      const q4 = ln.e.x - ln.s.x;
      const q5 = q1 * q3 + q2 * q4;
      if (Math.abs(q5) < 0.0001) return -1; /* 平行 */
      const q6 = ((ln.s.x - this.s.x) * q3 + (ln.s.y - this.s.y) * q4) / q5;
      pcPoint.x = this.s.x + q6 * q1;
      pcPoint.y = this.s.y + q6 * q2;
      // 端点チェック
      if (pcPoint.distanceTo(this.s) < tol) {
        rPoint.x = 1;
      } else if (pcPoint.distanceTo(this.e) < tol) {
        rPoint.x = 2;
      } else {
        const q7 = ((this.s.x - pcPoint.x) * (this.e.x - pcPoint.x) + (this.s.y - pcPoint.y) * (this.e.y - pcPoint.y));
        if (q7 < 0) rPoint.x = 0;
        else rPoint.x = -1;
      }
      if (pcPoint.distanceTo(ln.s) < tol) {
        rPoint.y = 1;
      } else if (pcPoint.distanceTo(ln.e) < tol) {
        rPoint.y = 2;
      } else {
        const q7 = ((ln.s.x - pcPoint.x) * (ln.e.x - pcPoint.x) + (ln.s.y - pcPoint.y) * (ln.e.y - pcPoint.y));
        if (q7 < 0) {
          rPoint.y = 0;
        } else {
          rPoint.y = -1;
        }
      }
      rc = 0;
      if (rPoint.x === -1) rc += 2;
      if (rPoint.y === -1) rc += 1;
      return rc;
    }

    /*------------------------------------------------------
    * 線分の中点を求める
    *
    */
    public GetMidPoint(): marietta.core.Point {
      if ((this.s == null) || (this.e == null)) {
        throw new Error('Line.GetMidPoint : 座標が設定されていません');
      }
      return new marietta.core.Point((this.s.x + this.e.x) / 2, (this.s.y + this.e.y) / 2, 0);
    }
}
